import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { CreateSupplierRequest, SupplierDto, UpdateSupplierRequest } from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import PartyStatement from './PartyStatement';
import Loading from './Loading';
import Hint from './Hint';
import ListFilter, { useListFilter } from './ListFilter';
import { matchesSearch } from './search';
import { formatMoney, sumAmounts } from './money';

const STATUSES = [
  { value: 'inactive', label: 'الموقوفين' },
  { value: 'all', label: 'الكل' },
] as const;

interface Props {
  companyId: string;
  currency: string;
  canCreate: boolean;
  /** المسح للمحاسب والمالك (`journal.manual`) — والخادم يرفض على أى حال. */
  canDelete: boolean;
  /** يُفتح كشفه مباشرة — من أعمار الموردين مثلاً. */
  initialSupplierId?: string | null;
  /** فتح كشف أو قفله يغيّر الرابط — فالرجوع يرجّع للقائمة. بلاه يبقى داخل الشاشة. */
  onSelectSupplier?: (id: string | null) => void;
  onSessionLost: () => void;
}

const EMPTY = {
  nameAr: '',
  taxRegistrationNumber: '',
  phone: '',
  email: '',
  paymentTermsDays: '0',
  isActive: true,
};

function draftOf(supplier: SupplierDto): typeof EMPTY {
  return {
    nameAr: supplier.nameAr,
    taxRegistrationNumber: supplier.taxRegistrationNumber ?? '',
    phone: supplier.phone ?? '',
    email: supplier.email ?? '',
    paymentTermsDays: String(supplier.paymentTermsDays),
    isActive: supplier.isActive,
  };
}

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) {
    const paths = caught.problem.issues?.map((issue) => issue.path ?? issue.message) ?? [];
    return (
      (caught.problem.detail ?? caught.problem.title) +
      (paths.length ? ': ' + paths.join('، ') : '')
    );
  }
  return caught instanceof Error ? caught.message : String(caught);
}

/** الموردون: قائمة وإضافة وتعديل وكشف — والمسح لمورد بلا حركات فقط — مرآة شاشة العملاء. الرصيد من الدفتر لا من المورد. */
export default function Suppliers({
  companyId,
  currency,
  canCreate,
  canDelete,
  initialSupplierId = null,
  onSelectSupplier,
  onSessionLost,
}: Props) {
  const [statementOf, setStatementOf] = useState<string | null>(initialSupplierId);
  // الرابط هو المرجع: الرجوع أو بحث يغيّر الطرف من بره فيتبعه الكشف
  useEffect(() => setStatementOf(initialSupplierId), [initialSupplierId]);
  const openStatement = (id: string | null) =>
    onSelectSupplier ? onSelectSupplier(id) : setStatementOf(id);
  // الكشف تحت القائمة: يُنزل إليه بعد ما تُرسم الشاشة (وبعد رجوع الصفحة لأعلاها)
  useEffect(() => {
    if (!statementOf) return;
    const timer = window.setTimeout(
      () => document.getElementById('party-statement')?.scrollIntoView({ block: 'start' }),
      200,
    );
    return () => window.clearTimeout(timer);
  }, [statementOf]);
  const [suppliers, setSuppliers] = useState<SupplierDto[] | null>(null);
  const [filter, setFilter] = useListFilter('', 'Suppliers');
  const [balances, setBalances] = useState<Map<string, string> | null>(null);
  // الافتراضى النشطون: المرحَّل ثم المعكوس يبقى موقوفًا بنفس الاسم، فلا يظهر مكررًا
  const shownSuppliers = (suppliers ?? []).filter(
    (supplier) =>
      (filter.status === 'all' ||
        (filter.status === 'inactive' ? !supplier.isActive : supplier.isActive)) &&
      matchesSearch(filter.query, [
        supplier.code,
        supplier.nameAr,
        supplier.phone,
        supplier.email,
        supplier.taxRegistrationNumber,
      ]),
  );
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState(EMPTY);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  // المورد الجارى تعديله — null: النموذج لإضافة مورد جديد
  const [editing, setEditing] = useState<SupplierDto | null>(null);

  const load = useCallback(() => {
    api
      .suppliers(companyId)
      .then(setSuppliers)
      .catch((caught: unknown) => {
        if (caught instanceof UnauthorizedError) onSessionLost();
        else setError(messageOf(caught));
      });
    // الرصيد من الأعمار (الدفتر كله) — من لا يقرأ التقارير لا يرى العمود
    api
      .apAging(companyId, new Date().toISOString().slice(0, 10))
      .then((aging) => setBalances(new Map(aging.rows.map((row) => [row.supplierId, row.total]))))
      .catch(() => setBalances(null));
  }, [companyId, onSessionLost]);

  useEffect(load, [load]);

  function update<K extends keyof typeof EMPTY>(key: K, value: (typeof EMPTY)[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
    setIdempotencyKey(crypto.randomUUID());
    setSaved(null);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    setError(null);
    const body: CreateSupplierRequest = {
      nameAr: draft.nameAr,
      taxRegistrationNumber: draft.taxRegistrationNumber,
      phone: draft.phone,
      email: draft.email,
      paymentTermsDays: Number(draft.paymentTermsDays),
    };
    try {
      if (editing) {
        const changes: UpdateSupplierRequest = { ...body, isActive: draft.isActive };
        const updated = await api.updateSupplier(companyId, editing.id, changes);
        setSaved('اتعدّل: ' + updated.code + ' — ' + updated.nameAr);
        setEditing(null);
        setDraft(EMPTY);
        load();
        return;
      }
      const created = await api.createSupplier(companyId, idempotencyKey, body);
      setSaved(created.code + ' — ' + created.nameAr);
      setDraft(EMPTY);
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(supplier: SupplierDto): void {
    setEditing(supplier);
    setDraft(draftOf(supplier));
    setSaved(null);
    setError(null);
    window.scrollTo(0, 0);
  }

  function cancelEdit(): void {
    setEditing(null);
    setDraft(EMPTY);
    setError(null);
  }

  async function remove(): Promise<void> {
    if (!editing) return;
    if (!window.confirm('مسح المورد «' + editing.nameAr + '» نهائيًا؟')) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteSupplier(companyId, editing.id);
      setSaved('اتمسح: ' + editing.code + ' — ' + editing.nameAr);
      if (statementOf === editing.id) openStatement(null);
      setEditing(null);
      setDraft(EMPTY);
      load();
    } catch (caught) {
      if (caught instanceof UnauthorizedError) onSessionLost();
      else if (
        caught instanceof ProblemError &&
        caught.problem.issues?.some((i) => i.code === 'IN_USE')
      )
        setError('المورد ده عليه حركات فمينفعش يتمسح — شيل علامة «نشط» واحفظ عشان توقفه.');
      else setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {canCreate && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>{editing ? 'تعديل المورد ' + editing.code : 'مورد جديد'}</h2>
          <div className="form-row">
            <label className="grow">
              الاسم
              <input
                value={draft.nameAr}
                onChange={(event) => update('nameAr', event.target.value)}
                maxLength={200}
              />
            </label>
            <label>
              التسجيل الضريبى
              <input
                value={draft.taxRegistrationNumber}
                onChange={(event) => update('taxRegistrationNumber', event.target.value)}
                inputMode="numeric"
                maxLength={32}
              />
              <Hint>رقم البطاقة الضريبية للمورد (٩ أرقام)، كما على فواتيره.</Hint>
            </label>
          </div>
          <div className="form-row">
            <label>
              الهاتف
              <input
                value={draft.phone}
                onChange={(event) => update('phone', event.target.value)}
                inputMode="tel"
                maxLength={32}
              />
            </label>
            <label className="grow">
              البريد
              <input
                type="email"
                value={draft.email}
                onChange={(event) => update('email', event.target.value)}
                maxLength={254}
              />
            </label>
            <label>
              مدة السداد (يوم)
              <input
                type="number"
                min={0}
                max={365}
                value={draft.paymentTermsDays}
                onChange={(event) => update('paymentTermsDays', event.target.value)}
              />
              <Hint>كم يومًا يمهلك المورد للدفع، فيذكّرك البرنامج بموعد السداد.</Hint>
            </label>
          </div>
          {editing && (
            <label className="option">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(event) => update('isActive', event.target.checked)}
              />
              <span>نشط</span>
              <Hint>
                المورد الموقوف بيختفى من قوايم الفواتير والمدفوعات، وكشف حسابه ورصيده فاضلين زى ما
                هم.
              </Hint>
            </label>
          )}
          {error && <p className="error">{error}</p>}
          {saved && <p className="sub">تم الحفظ: {saved}</p>}
          <div className="form-actions">
            <button
              type="submit"
              className="primary"
              disabled={saving || draft.nameAr.trim().length < 2}
            >
              {saving ? 'جارى الحفظ…' : editing ? 'حفظ التعديل' : 'حفظ المورد'}
            </button>
            {editing && (
              <button type="button" onClick={cancelEdit} disabled={saving}>
                إلغاء
              </button>
            )}
            {editing && canDelete && (
              <button
                type="button"
                className="danger"
                onClick={() => void remove()}
                disabled={saving}
              >
                مسح المورد
              </button>
            )}
          </div>
        </form>
      )}

      <div className="panel">
        <h2>الموردون</h2>
        {!canCreate && error && <p className="error">{error}</p>}
        {suppliers === null && !error && <Loading />}
        {suppliers?.length === 0 && <p className="sub">لا موردين بعد.</p>}
        {suppliers && suppliers.length > 0 && (
          <ListFilter
            filter={filter}
            onChange={setFilter}
            placeholder="دوّر بالاسم أو الكود أو التليفون أو التسجيل الضريبى"
            shown={shownSuppliers.length}
            total={suppliers.length}
            statuses={STATUSES}
            emptyStatusLabel="النشطين"
          />
        )}
        {suppliers && suppliers.length > 0 && shownSuppliers.length === 0 && (
          <p className="sub">مفيش نتايج للبحث ده.</p>
        )}
        {suppliers && shownSuppliers.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>الكود</th>
                  <th>الاسم</th>
                  <th>التسجيل الضريبى</th>
                  <th>الهاتف</th>
                  <th>مدة السداد</th>
                  {balances && <th>الرصيد</th>}
                  <th>الحالة</th>
                  {canCreate && <th />}
                </tr>
              </thead>
              <tbody>
                {shownSuppliers.map((supplier) => (
                  <tr
                    key={supplier.id}
                    className="clickable"
                    tabIndex={0}
                    aria-selected={statementOf === supplier.id}
                    onClick={() => openStatement(supplier.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') openStatement(supplier.id);
                    }}
                  >
                    <td className="mono">{supplier.code}</td>
                    <td>{supplier.nameAr}</td>
                    <td className="mono">{supplier.taxRegistrationNumber ?? ''}</td>
                    <td className="mono">{supplier.phone ?? ''}</td>
                    <td>{supplier.paymentTermsDays} يوم</td>
                    {balances && (
                      <td className="num-cell">{formatMoney(balances.get(supplier.id) ?? '0')}</td>
                    )}
                    <td>{supplier.isActive ? 'نشط' : <span className="pill">موقوف</span>}</td>
                    {canCreate && (
                      <td>
                        <button
                          type="button"
                          className="link"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEdit(supplier);
                          }}
                        >
                          تعديل
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {balances && (
                <tfoot>
                  <tr className="closing">
                    <td colSpan={5}>الإجمالى</td>
                    <td className="num-cell total">
                      {formatMoney(
                        sumAmounts(shownSuppliers.map((item) => balances.get(item.id) ?? '0')),
                      )}
                    </td>
                    <td colSpan={canCreate ? 2 : 1} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        {suppliers && suppliers.length > 0 && <p className="label">اضغط أى مورد لفتح كشف حسابه.</p>}
      </div>

      {statementOf && (
        <div id="party-statement">
          <PartyStatement
            key={statementOf}
            companyId={companyId}
            kind="supplier"
            partyId={statementOf}
            currency={currency}
            onSessionLost={onSessionLost}
          />
        </div>
      )}
    </>
  );
}
